import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ViewColumn as ViewColumnIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material'
import { ticketService } from '../../../../shared/services/TicketService'
import { Ticket, TicketStatus } from '../../../../shared/types/entities'
import { useAuth } from '@shared/auth/AuthContext'
import { OrganizationFilter } from '../../components/filters/OrganizationFilter'
import { useNotification } from '../../hooks/useSignalR'
import { TicketKanbanBoard } from '../../components/kanban/TicketKanbanBoard'
import { TicketDetailModal } from '../../components/ticket/TicketDetailModal'
import { CreateTicketModal } from '../../components/ticket/CreateTicketModal'

function AgentTickets() {
  const { user } = useAuth()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'All'>('All')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [childSiteFilter, setChildSiteFilter] = useState('')
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [view, setView] = useState<'table' | 'kanban'>(() => {
    const saved = localStorage.getItem('agentTicketsView')
    return (saved === 'kanban' || saved === 'table') ? saved : 'table'
  })

  const loadTickets = async () => {
    if (!user?.userId) return

    try {
      setLoading(true)
      setError(null)
      const filters = {
        status: statusFilter === 'All' ? undefined : statusFilter,
        search: searchQuery || undefined,
        assignedAgentId: user.userId,  // Only show tickets assigned to me
        organizationId: organizationFilter || undefined,
        childSiteId: childSiteFilter || undefined
      }
      const response = await ticketService.getAllTickets(1, 100, filters)
      setTickets(response.data)
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter, user?.userId])

  // Real-time notifications - refresh ticket list when tickets are created, updated, or assigned
  useNotification('TicketCreated', () => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter, user?.userId])

  useNotification('TicketAssigned', (data: any) => {
    // Refresh if ticket is assigned to current user
    if (data.assignedTo === user?.userId) {
      loadTickets()
    }
  }, [user?.userId, statusFilter, organizationFilter, childSiteFilter])

  useNotification('TicketStatusChanged', () => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter, user?.userId])

  useNotification('TicketUpdated', () => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter, user?.userId])

  const handleSearch = () => {
    loadTickets()
  }

  const handleOrganizationChange = (orgId: string, siteId: string) => {
    setOrganizationFilter(orgId)
    setChildSiteFilter(siteId)
  }

  const toggleView = () => {
    const newView = view === 'table' ? 'kanban' : 'table'
    setView(newView)
    localStorage.setItem('agentTicketsView', newView)
  }

  const handleStatusChange = async (ticketId: string, newStatus: TicketStatus) => {
    try {
      await ticketService.updateTicket(ticketId, { status: newStatus })
      await loadTickets() // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to update ticket status')
      throw err // Re-throw so the component can handle it
    }
  }

  const getStatusColor = (status: TicketStatus): 'default' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'Open':
        return 'warning'
      case 'InProgress':
        return 'info'
      case 'WaitingOnThirdParty':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Urgent':
        return '#d32f2f'
      case 'High':
        return '#f57c00'
      case 'Medium':
        return '#fbc02d'
      case 'Low':
        return '#388e3c'
      default:
        return '#757575'
    }
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          My Assigned Tickets
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={view === 'table' ? <ViewColumnIcon /> : <ViewListIcon />}
            onClick={toggleView}
          >
            {view === 'table' ? 'Kanban' : 'Table'}
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTickets} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Ticket
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ flex: 1 }}
              />
              <OrganizationFilter
                value={childSiteFilter || organizationFilter}
                onChange={handleOrganizationChange}
              />
            </Stack>
            <Tabs
              value={statusFilter}
              onChange={(_, newValue) => setStatusFilter(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="All" value="All" />
              <Tab label="Open" value="Open" />
              <Tab label="In Progress" value="InProgress" />
              <Tab label="Waiting on Third Party" value="WaitingOnThirdParty" />
              <Tab label="Resolved" value="Resolved" />
              <Tab label="Closed" value="Closed" />
            </Tabs>
          </Stack>
        </CardContent>
      </Card>

      {/* Tickets - Table or Kanban View */}
      {view === 'kanban' ? (
        <TicketKanbanBoard
          tickets={tickets}
          loading={loading}
          onTicketClick={(ticketId) => {
            setSelectedTicketId(ticketId)
            setModalOpen(true)
          }}
          onStatusChange={handleStatusChange}
        />
      ) : loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : tickets.length === 0 ? (
        <Alert severity="info">
          {statusFilter === 'All'
            ? 'You have no assigned tickets yet'
            : `You have no ${statusFilter.toLowerCase()} tickets`}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket #</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.ticketId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedTicketId(ticket.ticketId)
                    setModalOpen(true)
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {ticket.ticketNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.subject}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.priority}
                      size="small"
                      sx={{
                        backgroundColor: getPriorityColor(ticket.priority),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    {ticket.isGroupTicket && (
                      <Chip
                        label="Group"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {ticket.categoryName ? (
                      <Chip
                        label={ticket.categoryName}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#9c27b0', color: '#9c27b0' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status}
                      size="small"
                      color={getStatusColor(ticket.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.requesterName || 'Unknown'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.assignedAgentName || 'Unassigned'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(ticket.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        open={modalOpen}
        ticketId={selectedTicketId}
        onClose={() => {
          setModalOpen(false)
          setSelectedTicketId(null)
        }}
        onUpdate={() => {
          // Refresh ticket list when ticket is updated
          loadTickets()
        }}
      />

      {/* Create Ticket Modal */}
      <CreateTicketModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(ticketId) => {
          // Refresh ticket list and optionally open the new ticket
          loadTickets()
          setSelectedTicketId(ticketId)
          setModalOpen(true)
        }}
        userType="agent"
      />
    </Box>
  )
}

export default AgentTickets
